/**
 * Privacy Management Platform — Landing Page
 *
 * Font setup: this file loads IBM Plex Sans / IBM Plex Mono via an inline
 * <style> @import for drop-in convenience. For production, move this line
 * to your public/index.html <head> instead, for better load performance:
 *   <link rel="preconnect" href="https://fonts.googleapis.com">
 *   <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap" rel="stylesheet">
 *
 * Dependencies assumed (already used in the original file): react-router-dom, lucide-react, Tailwind CSS.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Lock,
  KeyRound,
  Eye,
  EyeOff,
  FileLock2,
  Database,
  Users,
  Activity,
  ScrollText,
  ClipboardCheck,
  Network,
  Layers3,
  Fingerprint,
  Timer,
  Building2,
  Scale,
  Globe,
  ArrowRight,
  ChevronDown,
  Check,
  Minus,
  Server,
  Search,
} from 'lucide-react';

const MONO = "'IBM Plex Mono', ui-monospace, monospace";

const NAV_LINKS = [
  { href: '#features', label: 'Features' },
  { href: '#workflow', label: 'How it works' },
  { href: '#security', label: 'Security' },
  { href: '#personas', label: 'Use cases' },
  { href: '#faq', label: 'FAQ' },
];

const PILLARS = [
  {
    id: 'vaulting',
    icon: Database,
    title: 'Data & document vaulting',
    description:
      'A single encrypted home for PII records and sensitive organizational files — no more scattered spreadsheets or shared drives.',
    items: [
      {
        icon: Database,
        title: 'PII record lifecycle',
        description:
          'Create, classify, search, and archive PII records, each linked to the person or entity it belongs to.',
      },
      {
        icon: FileLock2,
        title: 'Secure document vault',
        description:
          'Store contracts, compliance files, and internal records with encryption and controlled viewing sessions.',
      },
      {
        icon: Users,
        title: 'Employee & family mapping',
        description:
          'Track dependents, relationships, and organization-linked identities alongside primary user records.',
      },
    ],
  },
  {
    id: 'access',
    icon: KeyRound,
    title: 'Access control & masking',
    description:
      'Nobody sees more than they need. Every view is scoped, justified, and time-boxed.',
    items: [
      {
        icon: KeyRound,
        title: 'Role-based permissions',
        description:
          'Assign granular roles — viewer, approver, steward, auditor — so access always matches responsibility.',
      },
      {
        icon: Eye,
        title: 'Masked by default',
        description:
          'Sensitive fields display masked until a user requests and is granted full access for a stated purpose.',
      },
      {
        icon: Timer,
        title: 'Time-limited sessions',
        description:
          'Approved full-data views expire automatically, and every session is logged from open to close.',
      },
    ],
  },
  {
    id: 'audit',
    icon: ScrollText,
    title: 'Audit evidence',
    description:
      'A record of every access that holds up even after the underlying data changes or disappears.',
    items: [
      {
        icon: Activity,
        title: 'Independent audit pipeline',
        description:
          'Access events stream through Kafka into TimescaleDB, separate from the operational database.',
      },
      {
        icon: ClipboardCheck,
        title: 'ROPA mapping',
        description:
          'Connect processing activities to the records they touch, ready for a regulator or an internal review.',
      },
      {
        icon: ScrollText,
        title: 'Retention & revocation',
        description:
          'Run retention checks and process deletion or revocation requests without losing the evidence trail.',
      },
    ],
  },
  {
    id: 'consent',
    icon: Network,
    title: 'Consent & identity',
    description: 'PII collection stays tied to why it was collected, and who it belongs to.',
    items: [
      {
        icon: Network,
        title: 'Consent Platform integration',
        description:
          'Onboard organizations using a signed token, so PII collection always maps to an approved consent scope.',
      },
      {
        icon: Layers3,
        title: 'Tenant isolation',
        description:
          'Every organization operates inside its own schema-isolated workspace, with no shared boundaries.',
      },
      {
        icon: Fingerprint,
        title: 'Identity resolution',
        description:
          'Match records across systems to the right data principal, even across employee, external, and family identities.',
      },
    ],
  },
];

const STEPS = [
  {
    step: '01',
    title: 'Consent organization verified',
    description: 'The organization generates a PII onboarding token from the Consent Platform.',
  },
  {
    step: '02',
    title: 'PII request submitted',
    description: 'A public form sends only the onboarding token and admin contact details — nothing else.',
  },
  {
    step: '03',
    title: 'Platform admin approval',
    description: 'A platform admin reviews the request before any tenant schema, users, or roles are created.',
  },
  {
    step: '04',
    title: 'Workspace enabled',
    description: 'Admins manage records, documents, identities, audit views, and retention — all in one place.',
  },
];

const PERSONAS = [
  {
    icon: ShieldCheck,
    title: 'Security & SOC teams',
    description: 'Investigate access events, trace who viewed what, and close incidents with a complete evidence trail.',
  },
  {
    icon: Scale,
    title: 'Privacy & compliance teams',
    description: 'Map processing activities, respond to data subject requests, and stay ready for DPDP, GDPR, or internal audits.',
  },
  {
    icon: Building2,
    title: 'HR & people teams',
    description: 'Manage employee and family member data with the same access discipline as any other sensitive record.',
  },
  {
    icon: Search,
    title: 'Auditors & investigators',
    description: 'Pull a full, tamper-resistant history of access — even for records that have since been changed or removed.',
  },
];

const COMPARISON_ROWS = [
  { label: 'Central home for PII and documents', spreadsheets: false, point: true, platform: true },
  { label: 'Role-based access with masking', spreadsheets: false, point: 'partial', platform: true },
  { label: 'Audit trail survives record deletion', spreadsheets: false, point: false, platform: true },
  { label: 'Linked to consent evidence', spreadsheets: false, point: false, platform: true },
  { label: 'Tenant-isolated per organization', spreadsheets: false, point: 'partial', platform: true },
];

const FAQS = [
  {
    q: 'How is stored data protected?',
    a: 'PII records, uploaded documents, and metadata are encrypted at rest and in transit. Sensitive fields stay masked until a user is explicitly granted access for a stated purpose.',
  },
  {
    q: 'What happens to the audit trail if a record is edited or deleted?',
    a: 'Audit events are captured independently through a Kafka pipeline into TimescaleDB, separate from the operational database, so the evidence trail survives even after the source record changes.',
  },
  {
    q: 'How does masked vs. full access actually work?',
    a: 'Fields display masked by default. A user requests full access with a reason and purpose code, an approver grants it, and the system opens a time-limited, fully logged viewing session.',
  },
  {
    q: 'Can this track employee and family member data?',
    a: 'Yes. The identity layer maps employees, dependents, and organization-linked users, so HR and benefits use cases are supported alongside standard PII records.',
  },
  {
    q: 'How long does onboarding take?',
    a: 'Once your Consent Platform organization generates an onboarding token, a platform admin reviews the request before any workspace is provisioned — typically within one business day.',
  },
];

const LEDGER_LINES = [
  { time: '09:41:02', action: 'PII_VIEW', detail: 'masked · purpose:employment_verification', tag: 'MASKED' },
  { time: '09:41:14', action: 'ACCESS_REQUEST', detail: 'field:national_id · role:hr_admin', tag: 'PENDING' },
  { time: '09:41:19', action: 'ACCESS_APPROVED', detail: 'expires_in:14:32 · approver:d.steward', tag: 'APPROVED' },
  { time: '09:41:20', action: 'PII_VIEW', detail: 'full · session:sc_881a2', tag: 'FULL' },
  { time: '09:42:05', action: 'DOCUMENT_DOWNLOAD', detail: 'file:contract_2026.pdf · watermarked', tag: 'LOGGED' },
  { time: '09:43:11', action: 'SESSION_EXPIRED', detail: 'auto-revoked · view closed', tag: 'CLOSED' },
];

const PIPELINE_NODES = [
  { icon: Activity, label: 'App event' },
  { icon: Network, label: 'Kafka stream' },
  { icon: FileLock2, label: 'Signed envelope' },
  { icon: Server, label: 'Audit consumer' },
  { icon: Database, label: 'TimescaleDB' },
  { icon: Eye, label: 'Evidence view' },
];

function useInView(options) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, inView];
}

function Reveal({ children, className = '' }) {
  const [ref, inView] = useInView({ threshold: 0.15 });
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      {children}
    </div>
  );
}

export default function LandingPage() {
  const [activeFaq, setActiveFaq] = useState(0);
  const [revealed, setRevealed] = useState(false);

  return (
    <div
      style={{ fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif" }}
      className="min-h-screen bg-[#F6F7F9] text-slate-900"
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        @keyframes plexFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .ledger-line { animation: plexFadeUp 0.5s ease forwards; opacity: 0; }
        @media (prefers-reduced-motion: reduce) {
          .ledger-line { animation: none; opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-[#F6F7F9]/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-4 sm:px-8 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0E1420] text-[#FF9D57]">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-semibold text-slate-950">Privacy Management</p>
              <p
                className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                Platform
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 lg:flex">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href} className="transition hover:text-slate-950">
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <Link
              to="/login"
              className="rounded-lg px-3.5 py-2 text-sm font-medium text-slate-600 transition hover:bg-white hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2 sm:px-4"
            >
              Log in
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#FF9D57] px-4 py-2 text-sm font-semibold text-[#241206] shadow-sm shadow-orange-900/10 transition hover:bg-[#F2823A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
            >
              Request onboarding
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden px-6 pb-16 pt-14 sm:px-8 lg:px-10 lg:pt-20">
          <div className="mx-auto grid w-full max-w-7xl gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <div
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600"
                style={{ fontFamily: MONO }}
              >
                <Lock className="h-3.5 w-3.5 text-[#FF9D57]" />
                PII · DOCUMENTS · AUDIT EVIDENCE
              </div>

              <h1 className="max-w-2xl text-4xl font-semibold leading-[1.08] tracking-tight text-slate-950 sm:text-5xl xl:text-[3.4rem]">
                Prove who accessed your data — and why, every time.
              </h1>

              <p className="mt-6 max-w-xl text-base leading-7 text-slate-600 md:text-lg">
                One workspace to store PII and sensitive documents, control access by role and
                purpose, and generate audit evidence that survives even after records change or
                disappear.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#FF9D57] px-5 py-3 text-sm font-semibold text-[#241206] shadow-sm transition hover:bg-[#F2823A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
                >
                  Request onboarding
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
                >
                  Log in to workspace
                </Link>
              </div>

              <div className="mt-10 grid max-w-lg grid-cols-3 gap-3">
                <MetricCard label="Tenant model" value="Schema isolated" />
                <MetricCard label="Access model" value="RBAC + purpose" />
                <MetricCard label="Evidence" value="Survives deletion" />
              </div>
            </div>

            {/* Signature: live audit ledger panel */}
            <div className="relative rounded-3xl border border-slate-800/40 bg-[#0E1420] p-5 shadow-xl shadow-slate-900/10">
              <div
                className="pointer-events-none absolute inset-0 rounded-3xl opacity-[0.06]"
                style={{
                  backgroundImage:
                    'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                  backgroundSize: '22px 22px',
                }}
              />
              <div className="relative flex items-center justify-between border-b border-white/10 pb-4">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2F7A78] opacity-75 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#2F7A78]" />
                  </span>
                  <p
                    className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400"
                    style={{ fontFamily: MONO }}
                  >
                    Audit ledger · live
                  </p>
                </div>
                <p className="text-[11px] text-slate-500" style={{ fontFamily: MONO }}>
                  tenant:algosmic-corp
                </p>
              </div>

              <div className="relative mt-4 space-y-3 text-[12.5px] leading-relaxed" style={{ fontFamily: MONO }}>
                {LEDGER_LINES.map((line, i) => (
                  <div
                    key={line.time + line.action}
                    className="ledger-line flex items-start justify-between gap-3 border-b border-white/[0.06] pb-3 last:border-0"
                    style={{ animationDelay: `${i * 120}ms` }}
                  >
                    <div className="flex flex-1 items-start gap-3">
                      <span className="shrink-0 text-slate-500">{line.time}</span>
                      <span className="shrink-0 text-slate-200">{line.action}</span>
                      <span className="hidden truncate text-slate-500 sm:block">{line.detail}</span>
                    </div>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium tracking-wide ${
                        line.tag === 'APPROVED' || line.tag === 'FULL'
                          ? 'bg-[#FF9D57]/15 text-[#FFB27A]'
                          : line.tag === 'MASKED' || line.tag === 'PENDING'
                          ? 'bg-[#2F7A78]/20 text-[#7FC6C3]'
                          : 'bg-white/[0.08] text-slate-400'
                      }`}
                    >
                      {line.tag}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TRUST / COMPLIANCE BAR */}
        <section className="border-y border-slate-200 bg-white px-6 py-8 sm:px-8 lg:px-10">
          <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-4">
            <TrustBadge icon={Scale} label="DPDP Act–aligned" />
            <TrustBadge icon={Globe} label="GDPR-ready practices" />
            <TrustBadge icon={ShieldCheck} label="ISO 27001–aligned controls" />
            <TrustBadge icon={ScrollText} label="SOC-style audit evidence" />
          </div>
        </section>

        {/* FEATURES / PILLARS */}
        <section id="features" className="px-6 py-20 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">
            <Reveal className="max-w-2xl">
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                Core platform
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Four pillars, one workspace
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Data doesn't need five different tools to be safe. It needs one place that stores
                it, controls who sees it, and proves what happened.
              </p>
            </Reveal>

            <div className="mt-14 grid gap-6 md:grid-cols-2">
              {PILLARS.map((pillar) => (
                <Reveal key={pillar.id}>
                  <PillarCard pillar={pillar} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* MASKED / FULL VIEW DEMO */}
        <section className="px-6 pb-20 sm:px-8 lg:px-10">
          <div className="mx-auto grid w-full max-w-7xl gap-12 rounded-3xl border border-slate-200 bg-white p-8 lg:grid-cols-2 lg:items-center lg:p-12">
            <div>
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                Access, in practice
              </p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                Masked by default. Full access, on the record.
              </h3>
              <p className="mt-4 max-w-md text-sm leading-7 text-slate-600">
                Every sensitive field starts masked. When someone needs the real value, they
                state why — and the system logs the request, the approval, and the exact moment
                access ends.
              </p>
              <button
                onClick={() => setRevealed((v) => !v)}
                className="mt-6 inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
              >
                {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {revealed ? 'Mask fields again' : 'Try requesting access'}
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <p className="text-sm font-semibold text-slate-950">Employee record · E-10432</p>
                <span className="rounded-full bg-[#2F7A78]/10 px-2.5 py-1 text-[11px] font-medium text-[#2F7A78]">
                  {revealed ? 'Session active · 14:32 remaining' : 'Masked view'}
                </span>
              </div>

              <div className="mt-4 space-y-3">
                <FieldRow label="Full name" masked="A***a S****a" full="Aadhya Sharma" revealed={revealed} />
                <FieldRow label="Email" masked="a*****a@company.com" full="aadhya@company.com" revealed={revealed} />
                <FieldRow label="Phone" masked="+91 ****** 3210" full="+91 98765 43210" revealed={revealed} />
                <FieldRow label="National ID" masked="XXXX XXXX 8823" full="4821 5590 8823" revealed={revealed} />
              </div>

              {revealed && (
                <div className="mt-5 flex items-center gap-2 rounded-lg bg-[#FF9D57]/10 px-3 py-2 text-xs font-medium text-[#B65C1F]">
                  <KeyRound className="h-3.5 w-3.5" />
                  purpose: employment_verification · approved by d.steward
                </div>
              )}
            </div>
          </div>
        </section>

        {/* WORKFLOW */}
        <section id="workflow" className="border-y border-slate-200 bg-white px-6 py-20 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">
            <Reveal className="max-w-2xl">
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                How it works
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                From token to governed workspace
              </h2>
            </Reveal>

            <div className="relative mt-14 grid gap-8 md:grid-cols-4">
              <div className="absolute left-0 right-0 top-6 hidden h-px bg-slate-200 md:block" />
              {STEPS.map((step) => (
                <Reveal key={step.step} className="relative">
                  <div
                    className="relative z-10 flex h-12 w-12 items-center justify-center rounded-xl bg-[#0E1420] text-sm font-semibold text-[#FF9D57]"
                    style={{ fontFamily: MONO }}
                  >
                    {step.step}
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-slate-950">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{step.description}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* PERSONAS */}
        <section id="personas" className="px-6 py-20 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">
            <Reveal className="max-w-2xl">
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                Built for
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Whoever needs to answer for the data
              </h2>
            </Reveal>

            <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {PERSONAS.map((persona) => (
                <Reveal key={persona.title}>
                  <PersonaCard persona={persona} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* SECURITY / PIPELINE */}
        <section id="security" className="bg-[#0E1420] px-6 py-20 text-white sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
              <Reveal>
                <p
                  className="text-xs font-semibold uppercase tracking-[0.14em] text-[#7FC6C3]"
                  style={{ fontFamily: MONO }}
                >
                  Security & governance
                </p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                  Evidence that outlives the record
                </h2>
                <p className="mt-4 text-sm leading-7 text-slate-300">
                  Audit events don't live in the same database as the data they describe. Even if
                  a record is edited, deleted, or archived, the trail of who accessed it stays
                  intact.
                </p>
              </Reveal>

              <Reveal>
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                  {PIPELINE_NODES.map((node, i) => (
                    <React.Fragment key={node.label}>
                      <div className="flex min-w-[104px] flex-col items-center gap-2 text-center">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/[0.06] text-[#FF9D57]">
                          <node.icon className="h-5 w-5" />
                        </div>
                        <p className="text-[11px] font-medium leading-tight text-slate-300">{node.label}</p>
                      </div>
                      {i < PIPELINE_NODES.length - 1 && (
                        <ArrowRight className="h-4 w-4 shrink-0 text-slate-600" />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <SecurityPoint
                    title="Tenant isolation"
                    description="Each approved organization runs in its own schema-isolated workspace."
                  />
                  <SecurityPoint
                    title="Encrypted storage"
                    description="PII, documents, and evidence records are encrypted at rest and in transit."
                  />
                  <SecurityPoint
                    title="Token-only onboarding"
                    description="Public onboarding never exposes or trusts raw organization identifiers."
                  />
                  <SecurityPoint
                    title="Time-boxed access"
                    description="Full-data sessions expire automatically and close themselves out."
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* COMPARISON */}
        <section className="px-6 py-20 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl">
            <Reveal className="max-w-2xl">
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                Why teams switch
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Spreadsheets don't have an audit trail
              </h2>
            </Reveal>

            <Reveal className="mt-12 overflow-hidden rounded-2xl border border-slate-200">
              <ComparisonTable />
            </Reveal>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="border-y border-slate-200 bg-white px-6 py-20 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-3xl">
            <Reveal className="text-center">
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em] text-[#2F7A78]"
                style={{ fontFamily: MONO }}
              >
                Questions
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                Before you onboard
              </h2>
            </Reveal>

            <div className="mt-10 divide-y divide-slate-200 rounded-2xl border border-slate-200">
              {FAQS.map((faq, i) => (
                <FaqItem
                  key={faq.q}
                  faq={faq}
                  isOpen={activeFaq === i}
                  onToggle={() => setActiveFaq(activeFaq === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="px-6 py-20 sm:px-8 lg:px-10">
          <div className="mx-auto w-full max-w-7xl rounded-3xl bg-[#0E1420] px-8 py-14 text-center sm:px-16">
            <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Ready to give your data a home with a paper trail?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-slate-400">
              Generate an onboarding token from your Consent Platform organization and request
              access — provisioning starts only after admin approval.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-xl bg-[#FF9D57] px-6 py-3 text-sm font-semibold text-[#241206] shadow-sm transition hover:bg-[#F2823A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
              >
                Request onboarding
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/platform-admin/login"
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
              >
                Platform admin portal
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white px-6 py-10 sm:px-8 lg:px-10">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-4 text-sm text-slate-500 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#0E1420] text-[#FF9D57]">
              <ShieldCheck className="h-3.5 w-3.5" />
            </div>
            <span className="font-medium text-slate-700">Privacy Management Platform</span>
          </div>
          <p>© {new Date().getFullYear()} Privacy Management Platform. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400" style={{ fontFamily: MONO }}>
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-950">{value}</p>
    </div>
  );
}

function TrustBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
      <Icon className="h-4 w-4 text-[#2F7A78]" />
      {label}
    </div>
  );
}

function PillarCard({ pillar }) {
  const Icon = pillar.icon;
  return (
    <div className="h-full rounded-2xl border border-slate-200 bg-white p-7 transition hover:border-[#FF9D57]/40 hover:shadow-sm">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E1420] text-[#FF9D57]">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-5 text-lg font-semibold text-slate-950">{pillar.title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{pillar.description}</p>

      <ul className="mt-5 space-y-4 border-t border-slate-100 pt-5">
        {pillar.items.map((item) => (
          <li key={item.title} className="flex gap-3">
            <item.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#2F7A78]" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{item.title}</p>
              <p className="mt-0.5 text-sm leading-6 text-slate-600">{item.description}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PersonaCard({ persona }) {
  const Icon = persona.icon;
  return (
    <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2F7A78]/10 text-[#2F7A78]">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-950">{persona.title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{persona.description}</p>
    </div>
  );
}

function SecurityPoint({ title, description }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
      <p className="text-sm font-semibold text-white">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-slate-400">{description}</p>
    </div>
  );
}

function FieldRow({ label, masked, full, revealed }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white px-3.5 py-2.5">
      <span
        className="text-xs font-medium uppercase tracking-wide text-slate-400"
        style={{ fontFamily: MONO }}
      >
        {label}
      </span>
      <span
        className={`text-sm font-medium ${revealed ? 'text-slate-950' : 'text-slate-500'}`}
        style={{ fontFamily: MONO }}
      >
        {revealed ? full : masked}
      </span>
    </div>
  );
}

function ComparisonTable() {
  return (
    <table className="w-full border-collapse text-left text-sm">
      <thead>
        <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <th className="px-6 py-4 font-medium">Capability</th>
          <th className="px-6 py-4 font-medium">Spreadsheets & email</th>
          <th className="px-6 py-4 font-medium">Point tools</th>
          <th className="bg-[#0E1420] px-6 py-4 font-medium text-[#FF9D57]">This platform</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200">
        {COMPARISON_ROWS.map((row) => (
          <tr key={row.label}>
            <td className="px-6 py-4 font-medium text-slate-800">{row.label}</td>
            <td className="px-6 py-4">
              <ComparisonMark value={row.spreadsheets} />
            </td>
            <td className="px-6 py-4">
              <ComparisonMark value={row.point} />
            </td>
            <td className="bg-[#0E1420]/[0.03] px-6 py-4">
              <ComparisonMark value={row.platform} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ComparisonMark({ value }) {
  if (value === true) return <Check className="h-4 w-4 text-[#2F7A78]" />;
  if (value === 'partial') return <Minus className="h-4 w-4 text-slate-400" />;
  return <span className="text-slate-300">—</span>;
}

function FaqItem({ faq, isOpen, onToggle }) {
  return (
    <div className="px-6">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9D57] focus-visible:ring-offset-2"
      >
        <span className="text-sm font-semibold text-slate-950">{faq.q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && <p className="pb-5 text-sm leading-7 text-slate-600">{faq.a}</p>}
    </div>
  );
}